// utils/seoManager.js
import { generateSEOMetaTags } from "./seoHelper";

class SEOManager {
  // ===== CONSTANTS =====
  static BASE_URL = "https://tap2cash.in";
  static OG_IMAGE = "https://app.tap2cash.in/assets/logo.png";
  static DEFAULT_TITLE =
    "Sell Old Mobile Phones & Electronics | Best Price & Free Pickup | Tap2Cash";
  static DEFAULT_DESCRIPTION =
    "Sell your old mobile phones, laptops, tablets, smartwatches, and electronics at the best price in India. Get instant payment, free doorstep pickup, and 100% secure process. Trusted by over 1 million+ happy customers. Fast, reliable, and completely safe selling experience.";
  static DEFAULT_KEYWORDS =
    "sell old phones, sell electronics, Tap2Cash, gadget buyback, sell used devices, instant cash";

  generateSEO(route, params = {}) {
    const cleanRoute = route.split("?")[0];
    const staticSEO = this.getStaticSEO(cleanRoute);
    if (staticSEO) return staticSEO;
    return this.getDynamicSEO(cleanRoute, params);
  }

  getStaticSEO(route) {
    const pages = {
      "/": {
        title: "Sell, Buy & Repair Electronics Online in India | Tap2Cash",
        description:
          "Sell, buy & repair electronics online in India with Tap2Cash. Trusted consumer electronics platform for mobile phones, laptops, tablets, smartwatches and more. Get the best prices, free doorstep pickup and instant payment.",
        keywords:
          "Sell Electronics Online, Buy Electronics Online, Repair Electronics, Electronics Marketplace, Electronics Platform, Consumer Electronics",
        noIndex: false,
      },
      "/about-us": {
        title:
          "About Tap2Cash - India's Most Trusted Device Resale Platform Since 2020",
        description:
          "Learn about Tap2Cash - India's leading platform for selling used mobile phones, laptops, and electronics. We've served over 1 million customers across 500+ cities. Our mission is to make selling old devices simple, transparent, and rewarding.",
        keywords:
          "about Tap2Cash, sell phone online, electronics recycling, sustainable tech, trusted platform",
        noIndex: false,
      },
      "/buy-online": {
        title:
          "Buy Refurbished Mobile Phones Online | Best Deals | Quality Guaranteed | Tap2Cash",
        description:
          "Buy certified refurbished and used mobile phones online at the best prices in India. Each device undergoes rigorous quality testing, comes with warranty, and offers savings up to 70%. Shop confidently with our 7-day return policy.",
        keywords:
          "buy used phones, refurbished phones, second hand mobile, certified phones, quality phones",
        noIndex: false,
      },
      "/contact-us": {
        title: "Contact Us | Tap2Cash Customer Support 24/7",
        description:
          "Need help selling your old device? Contact Tap2Cash's dedicated customer support team for assistance with pricing, pickup scheduling, payment issues, or any other queries. We're here to help you get the best value for your device.",
        keywords:
          "contact support, customer care, Tap2Cash help, sell phone support, customer service",
        noIndex: false,
      },
      "/faqs": {
        title: "FAQs | Tap2Cash Help Center - Your Questions Answered",
        description:
          "Find comprehensive answers to frequently asked questions about selling your device on Tap2Cash. Learn about our pricing process, payment methods, doorstep pickup, device verification, security, and everything you need to know.",
        keywords:
          "faq, help center, sell phone questions, Tap2Cash help, device selling guide",
        noIndex: false,
      },
      "/login": {
        title: "Login | Tap2Cash - Secure Access to Your Account",
        description:
          "Securely login to your Tap2Cash account to manage your device sales, track orders, update payment details, and access your selling history. Your data is protected with advanced encryption.",
        keywords: "login, Tap2Cash login, account login, secure login",
        noIndex: true,
      },
      "/privacy-policy": {
        title: "Privacy Policy | Tap2Cash - Your Data Security Matters",
        description:
          "At Tap2Cash, we prioritize your privacy and data security. Read our comprehensive privacy policy to understand how we collect, use, store, and protect your personal information. We are committed to transparency and safeguarding your data.",
        keywords:
          "privacy policy, data protection, Tap2Cash privacy, GDPR compliance, data security",
        noIndex: true,
      },
      "/profile": {
        title: "My Profile | Tap2Cash Dashboard - Manage Your Account",
        description:
          "Manage your Tap2Cash profile from your personalized dashboard. Update your personal information, bank account details, view transaction history, and track all your device sales in one place.",
        keywords:
          "user profile, personal information, Tap2Cash dashboard, account management",
        noIndex: true,
      },
      "/request-confirmation": {
        title: "Service Request Confirmed | Tap2Cash - Next Steps",
        description:
          "Your service request has been successfully confirmed. Track your order status in real-time and get notified about every step of your device sale journey. We'll keep you updated on pickup and payment.",
        keywords:
          "service confirmation, request confirmed, Tap2Cash order, device pickup",
        noIndex: true,
      },
      "/sell-online": {
        title:
          "Sell Old Mobile Phone Online | Get Best Price | Free Pickup | Tap2Cash",
        description:
          "Sell your old mobile phone online at the best price in India. Get instant quote, free doorstep pickup, and immediate payment within 24 hours. We accept all brands including Apple, Samsung, OnePlus, Xiaomi, and more. Trusted by 1M+ customers.",
        keywords:
          "sell phone online, sell old mobile, instant cash for phone, sell used phone, mobile buyback",
        noIndex: false,
      },
      "/terms": {
        title: "Terms & Conditions | Tap2Cash Legal Policies",
        description:
          "Read Tap2Cash's complete terms of service, legal policies, and user agreements. Understand your rights, responsibilities, payment terms, shipping policies, and dispute resolution process when selling your device with us.",
        keywords:
          "terms and conditions, legal policy, Tap2Cash terms, user agreement, selling terms",
        noIndex: true,
      },
    };

    const seo = pages[route];
    if (seo) {
      return {
        ...seo,
        canonical: `${SEOManager.BASE_URL}${route}`,
        ogImage: SEOManager.OG_IMAGE,
        metaTags: generateSEOMetaTags(seo),
      };
    }
    return null;
  }

  getDynamicSEO(route, params) {
    const {
      categoryName,
      brandName,
      productName,
      variantName,
      productCount,
      maxPrice,
      categorySlug,
      brandSlug,
      productSlug,
      variantSlug,
      cityName,
      stateName,
      citySlug,
    } = params;

    const isCityRoute = route.includes("/in/");
    const cityDescPart = cityName
      ? ` in ${cityName}${stateName ? `, ${stateName}` : ""}`
      : "";
    const stateKeyword = stateName ? `, ${stateName}` : "";

    // ===== CATEGORY PAGE (Non-City) =====
    if (
      categorySlug &&
      !brandSlug &&
      !productSlug &&
      !variantSlug &&
      !isCityRoute
    ) {
      const name = categoryName || this.formatSlug(categorySlug);
      const title = `Sell Old ${name} Online & Get Instant Cash | Tap2Cash`;
      const keywords = `Sell Old ${name} Online, Sell Used ${name} Online, Sell ${name}`;
      const description = `Sell your old ${name} online with Tap2Cash. Select your ${name} brand to get an instant price estimate, enjoy free doorstep pickup and receive instant cash at the best price.`;
      return {
        title: title,
        description: description,
        keywords: keywords,
        canonical: `https://tap2cash.in/sell-used-online/${categorySlug}`,
        ogImage: "https://app.tap2cash.in/assets/logo.png",
        noIndex: false,
        metaTags: generateSEOMetaTags({
          title: title,
          description: description,
          keywords: keywords,
          canonical: `https://tap2cash.in/sell-used-online/${categorySlug}`,
          ogImage: "https://app.tap2cash.in/assets/logo.png",
          noIndex: false,
        }),
      };
    }

    // ===== CITY CATEGORY PAGE =====
    if (
      isCityRoute &&
      categorySlug &&
      !brandSlug &&
      !productSlug &&
      !variantSlug
    ) {
      const name = categoryName || this.formatSlug(categorySlug);
      const count = productCount || 0;

      let countText = "";
      if (count > 0) {
        countText = `Compare ${count}+ brands of ${name} available for sale${cityDescPart}. `;
      }
      const title = `Sell Used ${name} in ${cityName}${stateKeyword} | Best Prices & Free Pickup | Tap2Cash`;
      const description = `Sell your used ${name} at competitive prices in ${cityName}${stateKeyword}. ${countText}Get instant cash, free doorstep pickup, and secure payment. ${cityName}'s trusted platform for selling old ${name} online.`;
      const keywords = `sell ${name} in ${cityName}, used ${name} ${cityName}, ${name} price ${cityName}, ${name} buyer ${cityName}, recycle ${name} ${cityName}, ${cityName} electronics, ${stateName} ${name}`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/in/${citySlug}/${categorySlug}`;
      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== CITY BRAND PAGE =====
    if (isCityRoute && brandSlug && !productSlug && !variantSlug) {
      const category = categoryName || this.formatSlug(categorySlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const count = productCount || 0;

      let countText = "";
      if (count > 0) {
        countText = `Explore ${count} ${brand} ${category} models available in ${cityName}. `;
      }

      const title = `Sell ${brand} ${category} in ${cityName}${stateKeyword} | Best Prices & Free Pickup | Tap2Cash`;
      const description = `Sell your ${brand} ${category} at the best price in ${cityName}${stateKeyword}. ${countText}Get instant cash, free doorstep pickup, and 100% secure payment. ${cityName}'s trusted ${brand} buyer with guaranteed best rates.`;
      const keywords = `sell ${brand} in ${cityName}, ${brand} price ${cityName}, used ${brand} ${cityName}, ${brand} buyer ${cityName}, ${brand} ${category} ${cityName}, ${cityName} electronics, ${stateName} ${brand}`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/in/${citySlug}/${categorySlug}/${brandSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== CITY PRODUCT PAGE =====
    if (isCityRoute && productSlug && !variantSlug) {
      const product = productName || this.formatSlug(productSlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const category = categoryName || this.formatSlug(categorySlug);
      const priceText =
        maxPrice > 0 ? ` up to ₹${maxPrice.toLocaleString()}` : "";
      const variantCount = params.variantsCount || 0;

      let variantText = "";
      if (variantCount > 0) {
        variantText = `${variantCount} configurations available for your ${brand} ${product}. `;
      }

      const title = `Sell ${brand} ${product} in ${cityName}${stateKeyword}${priceText} | Free Pickup | Tap2Cash`;
      const description = `Sell your ${brand} ${product} in ${cityName}${stateKeyword}${priceText}. ${variantText}Get instant cash, free doorstep pickup, and secure payment. ${cityName}'s trusted platform for selling ${brand} ${category} with the best market rates.`;
      const keywords = `sell ${brand} ${product} in ${cityName}, ${brand} ${product} price ${cityName}, used ${brand} ${product} ${cityName}, ${product} buyer ${cityName}, ${cityName} ${brand} dealer, ${stateName} electronics`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/in/${citySlug}/${categorySlug}/${brandSlug}/${productSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== CITY VARIANT PAGE =====
    if (isCityRoute && variantSlug) {
      const variant = variantName || this.formatSlug(variantSlug);
      const product = productName || this.formatSlug(productSlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const price =
        maxPrice > 0 ? `₹${maxPrice.toLocaleString()}` : "best price";

      const title = `Sell ${brand} ${product} ${variant} in ${cityName}${stateKeyword} - Get ${price} | Tap2Cash`;
      const description = `Sell your ${brand} ${product} ${variant} at ${price} in ${cityName}${stateKeyword}. Get instant cash, free doorstep pickup, and 100% secure payment. ${cityName}'s trusted buyer for ${brand} ${product} with guaranteed best rates.`;
      const keywords = `sell ${brand} ${product} ${variant} in ${cityName}, ${brand} ${product} ${variant} price ${cityName}, used ${variant} ${cityName}, ${variant} buyer ${cityName}, ${cityName} ${brand} ${product}, ${stateName} ${variant}`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/in/${citySlug}/${categorySlug}/${brandSlug}/${productSlug}/${variantSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== NON-CITY BRAND PAGE =====
    if (brandSlug && !productSlug && !variantSlug && !isCityRoute) {
      const category = categoryName || this.formatSlug(categorySlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const count = productCount || 0;

      let countText = "";
      if (count > 0) {
        countText = `Choose from ${count} ${brand} ${category} models and get instant price estimates. `;
      }

      const title = `Sell ${brand} ${category} Online | Best Prices & Free Pickup | Tap2Cash`;
      const description = `Sell your ${brand} ${category} at the best price in India. ${countText}Get instant cash, free doorstep pickup, and 100% secure payment. ${brand} ${category} buyback with guaranteed market-leading prices.`;
      const keywords = `sell ${brand} ${category}, ${brand} price, used ${brand}, ${brand} buyback, sell ${brand} phone, ${category} selling, ${brand} resale`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/${categorySlug}/${brandSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== NON-CITY PRODUCT PAGE =====
    if (productSlug && !variantSlug && !isCityRoute) {
      const product = productName || this.formatSlug(productSlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const category = categoryName || this.formatSlug(categorySlug);
      const priceText =
        maxPrice > 0 ? ` up to ₹${maxPrice.toLocaleString()}` : "";
      const variantCount = params.variantsCount || 0;

      let variantText = "";
      if (variantCount > 0) {
        variantText = `${variantCount} variants available for your ${brand} ${product}. `;
      }

      const title = `Sell ${brand} ${product}${priceText} | Best Price & Free Pickup | Tap2Cash`;
      const description = `Sell your ${brand} ${product}${priceText} in India. ${variantText}Get instant cash, free doorstep pickup, and 100% secure payment. Trusted by 1M+ customers for ${brand} ${category} buyback with guaranteed best rates.`;
      const keywords = `sell ${brand} ${product}, ${brand} ${product} price, used ${brand} ${product}, ${product} buyback, sell used ${product}, ${brand} resale, gadget selling`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/${categorySlug}/${brandSlug}/${productSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== NON-CITY VARIANT PAGE =====
    if (variantSlug && !isCityRoute) {
      const variant = variantName || this.formatSlug(variantSlug);
      const product = productName || this.formatSlug(productSlug);
      const brand = brandName || this.formatSlug(brandSlug);
      const price =
        maxPrice > 0 ? `₹${maxPrice.toLocaleString()}` : "best price";

      const title = `Sell ${brand} ${product} ${variant} - Get ${price} | Best Price & Free Pickup | Tap2Cash`;
      const description = `Sell your ${brand} ${product} ${variant} at ${price} in India. Get instant cash, free doorstep pickup, and 100% secure payment. ${brand} ${product} ${variant} buyback with guaranteed best market rates and quick processing.`;
      const keywords = `sell ${brand} ${product} ${variant}, ${brand} ${product} ${variant} price, used ${variant}, ${variant} buyback, sell ${variant}, ${brand} ${product} resale, gadget exchange`;
      const canonical = `${SEOManager.BASE_URL}/sell-used-online/${categorySlug}/${brandSlug}/${productSlug}/${variantSlug}`;

      return this.createSEOResponse({
        title,
        description,
        keywords,
        canonical,
        noIndex: false,
      });
    }

    // ===== RETURN NULL FOR UNMATCHED ROUTES =====
    return null;
  }

  // ===== HELPER METHOD TO CREATE SEO RESPONSE =====
  createSEOResponse({
    title,
    description,
    keywords,
    canonical,
    noIndex = false,
  }) {
    const seoData = {
      title,
      description,
      keywords,
      canonical,
      ogImage: SEOManager.OG_IMAGE,
      noIndex,
      metaTags: generateSEOMetaTags({
        title,
        description,
        keywords,
        canonical,
        ogImage: SEOManager.OG_IMAGE,
        noIndex,
      }),
    };
    return seoData;
  }

  formatSlug(slug) {
    if (!slug) return "";
    return slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
}

export const seoManager = new SEOManager();
